import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from './api';
import type { InvoiceDraftInput, Settings } from './types';
import { notifyInvoiceSuccess } from './lib/error-notifications';

export const invoiceKeys = {
  all: ['invoices'] as const,
  overview: ['invoices', 'overview'] as const,
  list: (params: Record<string, unknown>) => ['invoices', 'list', params] as const,
  detail: (id: string) => ['invoices', 'detail', id] as const,
  payments: (params: Record<string, unknown>) => ['invoices', 'payments', params] as const,
  payment: (id: string) => ['invoices', 'payment', id] as const,
  customFields: ['invoices', 'custom-fields'] as const,
};
/** The company's custom fields for invoices; none when they cannot be read. */
export const useInvoiceCustomFields = () =>
  useQuery({
    queryKey: invoiceKeys.customFields,
    queryFn: invoicesApi.customFields,
    staleTime: 60_000,
    retry: false,
  });
export const useOverview = (options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: invoiceKeys.overview,
    queryFn: invoicesApi.overview,
    enabled: options.enabled ?? true,
  });
export const useInvoices = (params: Record<string, string | number | undefined>) =>
  useQuery({ queryKey: invoiceKeys.list(params), queryFn: () => invoicesApi.list(params) });
export const useInvoice = (id?: string) =>
  useQuery({
    queryKey: invoiceKeys.detail(id ?? ''),
    queryFn: () => invoicesApi.detail(id!),
    enabled: Boolean(id),
  });
export const useCreateInvoice = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: InvoiceDraftInput) => invoicesApi.create(input),
    onSuccess: () => client.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
};
export const useInvoiceCommand = (id: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ command, body }: { command: string; body: Record<string, unknown> }) =>
      invoicesApi.command(id, command, body),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      client.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
};
export const usePayments = (params: Record<string, string | number | undefined>) =>
  useQuery({ queryKey: invoiceKeys.payments(params), queryFn: () => invoicesApi.payments(params) });
export const usePayment = (id?: string) =>
  useQuery({
    queryKey: invoiceKeys.payment(id ?? ''),
    queryFn: () => invoicesApi.payment(id!),
    enabled: Boolean(id),
  });
export const useSettings = () =>
  useQuery({ queryKey: ['invoices', 'settings'], queryFn: invoicesApi.settings });
export const useSaveSettings = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (settings: Settings) => invoicesApi.saveSettings(settings),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: invoiceKeys.all });
      notifyInvoiceSuccess('Invoice configuration saved');
    },
  });
};
